<script lang="ts">
  /**
   * One exposure counter card. The card *is* the exposure switch: clicking it selects its
   * exposure filter, clicking it while active clears the filter (spec 6.2). A document that is
   * both public and externally shared is counted under all three cards, so the three numbers
   * deliberately do not sum to the row count.
   *
   * The number is a count of documents, and says so; the footer names who can open them.
   */
  import { formatNumberWithCommas } from "$lib/utils/number";

  import { exposureFilterLabel, PROVISIONAL_LABEL, type ExposureFilter } from "../exposedSensitiveDocuments";
  import { EXPOSURE_TONE } from "./exposureTones";

  let {
    kind,
    count,
    provisional = false,
    active = false,
    onSelect,
  }: {
    kind: ExposureFilter;
    count: number;
    /** The rows are still loading, so `count` is only the rows so far (#2265). */
    provisional?: boolean;
    active?: boolean;
    /** Called with the card's kind, or `null` when the active card is clicked again. */
    onSelect: (kind: ExposureFilter | null) => void;
  } = $props();

  const tone = $derived(EXPOSURE_TONE[kind]);
  const label = $derived(exposureFilterLabel(kind));
</script>

<button
  type="button"
  class="rounded-xl border text-left shadow-sm transition-colors p-4 {active
    ? tone.selected
    : `bg-white border-gray-200 ${tone.hover}`}"
  aria-pressed={active}
  onclick={() => onSelect(active ? null : kind)}
>
  <div
    class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide {active ? '' : 'text-gray-500'}"
  >
    <span class="h-2 w-2 rounded-full {tone.dot}"></span>{label}
  </div>
  <div class="flex items-baseline gap-1.5">
    <div class="text-4xl font-black tabular-nums leading-tight">
      {formatNumberWithCommas(count)}{#if provisional}<span
          class="ml-1 text-xs font-medium {active ? 'opacity-80' : 'text-gray-500'}"
          data-testid="provisional-count">{PROVISIONAL_LABEL}</span
        >{/if}
    </div>
    <span class="text-sm font-medium {active ? 'opacity-80' : 'text-gray-500'}" data-testid="counter-card-unit"
      >{count === 1 ? "document" : "documents"}</span
    >
  </div>
  <div class="mt-2 border-t border-black/5 pt-2 text-xs" data-testid="counter-card-accessible-by">
    <span class={active ? "opacity-80" : "text-gray-500"}>Accessible by</span>
    {tone.accessibleBy}{active ? " · click to clear" : ""}
  </div>
</button>
